import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import ReactCountryFlag from "react-country-flag";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import TopPlayerWidget from "./TopPlayerWidget";
import Topbar from "./Topbar";
import Footer from "./Footer";
import Loader from "./Loader";
const apiBase = process.env.REACT_APP_API_URL;

const MatchHistoryPage = () => {
  const { leagueId, matchId } = useParams();
  const navigate = useNavigate();
  const matchRefs = useRef({});
  const [matches, setMatches] = useState([]);
  const [topPlayers, setTopPlayers] = useState([]);
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedMatch, setExpandedMatch] = useState(null);
  const [expandedGame, setExpandedGame] = useState(null);
  // const regionBanners = {
  //   LCK: "/banners/LCK-banner.png",
  //   LEC: "/banners/LEC-banner.png",
  //   LPL: "/banners/LPL-banner.png",
  //   LCP: "/banners/LCP-banner.png",
  //   FIRST: "/banners/FIRST-banner.png",
  // };
  const TOURNAMENT_NAMES = {
    FIRST: "First Stand",
    LPL: "LPL",
    LCK: "LCK",
    LEC: "LEC",
    LCP: "LCP",
    MSI: "Mid-Season Invitational",
    Worlds: "World Championship",
  };
  const ROLE_ORDER = {
    Top: 1,
    Jng: 2,
    Mid: 3,
    Bot: 4,
    Sup: 5,
  };

  useEffect(() => {
    if (!leagueId) return;

    const loadData = async () => {
      try {
        setLoading(true);

        const [matchesRes, topPlayersRes, tournamentRes] = await Promise.all([
          axios.post(`${apiBase}/api/matches/history`, { leagueId, limit: 15 }),
          axios.get(`${apiBase}/api/league/${leagueId}/top-players`),
          axios.get(`${apiBase}/api/league/${leagueId}/tournament`),
        ]);

        setMatches(matchesRes.data || []);
        setTopPlayers(topPlayersRes.data || []);
        setTournament(tournamentRes.data?.tournament || null);
      } catch (err) {
        console.error("Failed to load match history or top players:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [leagueId]);

  useEffect(() => {
    if (!matchId || matches.length === 0) return;

    setExpandedMatch(matchId);
  }, [matchId, matches]);

  useEffect(() => {
    if (!expandedMatch) return;

    const tryScroll = () => {
      const el = matchRefs.current[expandedMatch];
      if (!el) return false;

      const rect = el.getBoundingClientRect();

      const offset = rect.top + window.scrollY - window.innerHeight / 2 + rect.height / 2;

      window.scrollTo({
        top: offset,
        behavior: "smooth",
      });

      return true;
    };

    // Try immediately
    if (tryScroll()) return;

    // Retry until element is mounted
    const interval = setInterval(() => {
      if (tryScroll()) {
        clearInterval(interval);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [expandedMatch]);

  const [countryCode, city] = tournament ? (tournament.location || "").split(",") : [null, null];

  function getDisplayName(tournamentName) {
    if (!tournamentName) return tournamentName;
    // Split off the year — assumes format "KEY YEAR"
    const parts = tournamentName.split(" ");
    const year = parts[parts.length - 1];
    const key = parts.slice(0, -1).join(" ");
    const mapped = TOURNAMENT_NAMES[key];
    return mapped ? `${mapped} ${year}` : tournamentName;
  }

  const sortByRole = (players = []) => {
    return [...players].sort((a, b) => {
      return (ROLE_ORDER[a.role] || 99) - (ROLE_ORDER[b.role] || 99);
    });
  };

  //   const now = new Date();

  const classifiedMatches = matches.reduce(
    (acc, match) => {
      const firstGame = match.games?.[0];
      if (!firstGame) return acc;

      const blueTeam = firstGame.blue_team;
      const redTeam = firstGame.red_team;

      const seriesScore = match.series_score || {};
      const blueScore = seriesScore[blueTeam.id] || 0;
      const redScore = seriesScore[redTeam.id] || 0;

      const winsNeeded = Math.ceil(match.best_of / 2);
      //   const matchStart = new Date(match.match_date);

      //   const isStarted = now >= matchStart;
      const isCompleted = blueScore >= winsNeeded || redScore >= winsNeeded;

      if (isCompleted) {
        acc.completed.push(match);
      } else {
        acc.active.push(match); // upcoming OR live
      }

      return acc;
    },
    { active: [], completed: [] },
  );

  const renderMatch = (match) => {
    const firstGame = match.games?.[0];
    if (!firstGame) return null;

    const blueTeam = firstGame.blue_team;
    const redTeam = firstGame.red_team;

    const blueTeamId = firstGame.blue_team.id;
    const redTeamId = firstGame.red_team.id;

    const seriesScore = match.series_score || {};
    const blueScore = seriesScore[blueTeam.id] || 0;
    const redScore = seriesScore[redTeam.id] || 0;

    const winsNeeded = Math.ceil(match.best_of / 2);
    const matchStart = new Date(match.match_date);
    const now = new Date();

    const isStarted = now >= matchStart;
    const isCompleted = blueScore >= winsNeeded || redScore >= winsNeeded;

    const isLive = isStarted && !isCompleted;

    return (
      <>
        <div
          key={match.match_id}
          className="match-prediction-container-history"
          style={{ cursor: "pointer" }}
          onClick={() => setExpandedMatch(expandedMatch === String(match.match_id) ? null : String(match.match_id))}
          ref={(el) => (matchRefs.current[String(match.match_id)] = el)}>
          <div
            style={{
              display: "flex",
              gap: "0px",
              flexWrap: "wrap",
              minWidth: "100px",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
            className="match-prediction-region-container-history">
            {/* {tournament && (
              <img
                src={`/banners/${tournament.banner_image}`}
                alt={tournament.name}
                style={{
                  height: "18px",
                  width: "18px",
                  objectFit: "contain",
                }}
              />
            )} */}
            <span style={{ fontSize: "12px", color: "#9ca3af" }}>{formatMatchDate(match.match_date)}</span>
          </div>
          <div className="prediction-slip-teams-history" style={{ padding: "6px 10px", cursor: "pointer" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px" }}>
              <div style={{ color: "rgb(190,205,227)", fontSize: "12px", textAlign: "end" }}>{blueTeam.name}</div>
              <img src={`/teams/${blueTeam.logo}`} alt={blueTeam.name} style={{ height: "22px", objectFit: "contain", width: "22px" }} />
            </div>
            <div className="series-display">
              {!isStarted ? (
                <div style={{ fontWeight: "700", color: "white", fontSize: "12px" }}>BO{match.best_of}</div>
              ) : (
                <div className={`series-score-${isLive ? "live" : "completed"}`}>
                  <span className={blueScore > redScore ? "score winner" : blueScore < redScore ? "score loser" : "score"}>{blueScore}</span>

                  <span className="score-separator">:</span>

                  <span className={redScore > blueScore ? "score winner" : redScore < blueScore ? "score loser" : "score"}>{redScore}</span>
                </div>
              )}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: "12px" }}>
              <img src={`/teams/${redTeam.logo}`} alt={redTeam.name} style={{ height: "22px", objectFit: "contain", width: "22px" }} />
              <div style={{ color: "rgb(190,205,227)", fontSize: "12px" }}>{redTeam.name}</div>
            </div>
          </div>
          <ChevronDown
            color="rgb(190, 205, 227)"
            onClick={() => setExpandedMatch(expandedMatch === String(match.match_id) ? null : String(match.match_id))}
          />
        </div>
        {/* COLLAPSIBLE GAMES */}
        {expandedMatch === String(match.match_id) && (
          <div className="games-container" style={{ marginBottom: "6px", display: "flex", gap: "2px", flexDirection: "column" }}>
            {match.games?.map((game) => {
              const gameLeftPlayers = game.blue_team.id === blueTeamId ? game.blue_players : game.red_players;

              const gameRightPlayers = game.blue_team.id === redTeamId ? game.blue_players : game.red_players;

              const gameLeftKills = game.blue_team.id === blueTeamId ? game.blue_team_kills : game.red_team_kills;

              const gameRightKills = game.blue_team.id === redTeamId ? game.blue_team_kills : game.red_team_kills;

              const isLeftBlue = game.blue_team.id === blueTeamId;

              const leftTeamName = isLeftBlue ? game.blue_team.name : game.red_team.name;
              const rightTeamName = isLeftBlue ? game.red_team.name : game.blue_team.name;

              const leftColor = isLeftBlue ? "#5d87da" : "#da745d";
              const rightColor = isLeftBlue ? "#da745d" : "#5d87da";

              const leftTeamId = isLeftBlue ? game.blue_team.id : game.red_team.id;
              const rightTeamId = isLeftBlue ? game.red_team.id : game.blue_team.id;

              const leftWon = game.winner_team_id === leftTeamId;
              const rightWon = game.winner_team_id === rightTeamId;

              return (
                <div key={game.game_id} className="game-card">
                  <div className="game-header" onClick={() => setExpandedGame(expandedGame === game.game_id ? null : game.game_id)}>
                    <h4 style={{ fontSize: "12px", color: "#becde3", minWidth: "100px" }}>Map {game.game_number}</h4>

                    <div className="prediction-slip-teams-history">
                      {/* LEFT TEAM CHAMPS */}
                      <div className="champions-blue">
                        {sortByRole(gameLeftPlayers)?.map((p) => (
                          <div
                            key={p.player_id}
                            style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "4px",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}>
                            <img
                              src={`/champion/${p.champion_played}.png`}
                              alt={p.champion_played}
                              style={{
                                width: "120%",
                                height: "120%",
                                objectFit: "cover",
                                marginLeft: "-10%",
                                marginTop: "-10%",
                              }}
                            />
                          </div>
                        ))}
                      </div>

                      {/* CENTER INFO */}
                      {/* CENTER INFO */}
                      <div
                        className="game-center"
                        style={{ minWidth: "65px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
                        <div className="game-kills" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span style={{ fontSize: "14px", width: "16px", textAlign: "center", visibility: leftWon ? "visible" : "hidden" }}>🏆</span>
                          <span
                            style={{
                              height: "8px",
                              width: "8px",
                              background: game.blue_team.id === blueTeamId ? "#5d87da" : "#da745d",
                              display: "block",
                              borderRadius: "90px",
                              flexShrink: 0,
                            }}
                          />
                          <p style={{ color: "white", fontSize: "14px", fontWeight: "600", whiteSpace: "nowrap", margin: "0", minWidth: "42px" }}>
                            {gameLeftKills} : {gameRightKills}
                          </p>
                          <span
                            style={{
                              height: "8px",
                              width: "8px",
                              background: game.red_team.id === redTeamId ? "#da745d" : "#5d87da",
                              display: "block",
                              borderRadius: "90px",
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ fontSize: "14px", width: "16px", textAlign: "center", visibility: rightWon ? "visible" : "hidden" }}>
                            🏆
                          </span>
                        </div>
                        <p style={{ margin: "0", color: "rgb(156, 163, 175)", fontSize: "12px", letterSpacing: "-0.4px" }}>Details &#9662;</p>
                      </div>
                      {/* RIGHT TEAM CHAMPS */}
                      <div className="champions-red">
                        {sortByRole(gameRightPlayers)?.map((p) => (
                          <div
                            key={p.player_id}
                            style={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "4px",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}>
                            <img
                              src={`/champion/${p.champion_played}.png`}
                              alt={p.champion_played}
                              style={{
                                width: "120%",
                                height: "120%",
                                objectFit: "cover",
                                marginLeft: "-10%",
                                marginTop: "-10%",
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                    <span style={{ width: "20px" }} />
                  </div>

                  {expandedGame === game.game_id && (
                    <div className="game-details">
                      <span style={{ width: "100px" }} className="filler-block" />
                      {/* LEFT TEAM */}
                      <div className="team-block" style={{ alignItems: "flex-start" }}>
                        <div
                          style={{
                            color: leftColor,
                            fontWeight: "700",
                            marginBottom: "10px",
                          }}>
                          {leftTeamName}
                        </div>

                        <PlayerTable players={gameLeftPlayers} />
                      </div>

                      {/* RIGHT TEAM */}
                      <div className="team-block" style={{ alignItems: "flex-end" }}>
                        <div
                          style={{
                            color: rightColor,
                            fontWeight: "700",
                            marginBottom: "10px",
                          }}>
                          {rightTeamName}
                        </div>

                        <PlayerTable players={gameRightPlayers} side="red" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  const formatMatchDate = (dateString) => {
    const date = new Date(dateString);

    const datePart = new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
    }).format(date);

    const timePart = new Intl.DateTimeFormat("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);

    return (
      <>
        {datePart} at <br />
        {timePart}
      </>
    );
  };

  if (loading) return <Loader loadingTitle={"Loading Match Results"} />;
  // if (!matches.length)
  //   return (
  //     <div className="match-history-empty" style={{ color: "white", textAlign: "center", fontSize: "21px", fontWeight: "700", marginTop: "60px" }}>
  //       No matches found for this league.
  //     </div>
  //   );

  return (
    <div className="page-container" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Topbar leagueId={leagueId} showLinks={true} showBudget={true} />
      <div className="main">
        {tournament && (
          <div
            className="tournament-header"
            style={{
              background: `
rgb(7 13 23)`,
              overflow: "hidden",
            }}>
            <img
              src={`/banners/${tournament.banner_image}`}
              alt={tournament.name}
              style={{
                position: "absolute",
                height: "380px",
                width: "380px",
                objectFit: "contain",
                opacity: "0.04",
                right: "0px",
                bottom: "-100px",
                zIndex: "1",
              }}
              className="team-bg-logo"
            />
            <div className="tournament-header-left">
              <img src={`/banners/${tournament.banner_image}`} alt={tournament.name} className="tournament-banner-icon" />
              <div className="tournament-title-row">
                <h2 className="tournament-name">{getDisplayName(tournament.name)}</h2>
                <span className="tournament-dates">
                  {new Date(tournament.start_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  {" — "}
                  {new Date(tournament.end_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>
            </div>
            {countryCode && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "2px" }}>
                <span className="tournament-location" style={{ gap: "4px", display: "flex", alignItems: "center" }}>
                  <ReactCountryFlag
                    countryCode={countryCode.trim()}
                    svg
                    cdnUrl="https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/1x1/"
                    cdnSuffix="svg"
                    title={city?.trim()}
                    style={{ height: "16px", borderRadius: "4px", width: "16px" }}
                  />
                  {city && <span>{city.trim()}</span>}
                </span>
                <span style={{ color: "rgba(255, 255, 255, 0.6)", fontWeight: "700", fontSize: "12px" }}>Location</span>
              </div>
            )}
          </div>
        )}
        <div style={{ display: "flex", gap: "10px", maxWidth: "1400px", margin: "0 auto 20px auto" }} className="esports-match-history-container">
          <div className="desktop-predictions-history" style={{ flex: "1 1" }}>
            {/* LIVE / UPCOMING */}
            {classifiedMatches.active.length > 0 && (
              <>
                <h3 className="section-title">
                  <span>MATCHES</span>
                </h3>
                {classifiedMatches.active.reverse().map(renderMatch)}
              </>
            )}
            {/* COMPLETED */}
            {classifiedMatches.completed.length > 0 && (
              <>
                <h3 className="section-title completed">RECENT MATCHES</h3>
                {classifiedMatches.completed.map(renderMatch)}
              </>
            )}
          </div>
          <div
            style={{
              height: "fit-content",
              //   background: "#161f2d",
              borderRadius: "4px",
              boxSizing: "border-box",
              // padding: "0 10px",
              display: "flex",
              flexDirection: "column",
              gap: "10px",
            }}
            className="esports-match-history-top-players">
            <TopPlayerWidget leagueId={leagueId} tournamentName={tournament?.name} />
            <div style={{ background: "#ffffff05", padding: "14px", border: "1px solid #19232f" }}>
              <span className="avg-label" style={{ marginBottom: "10px", display: "block" }}>
                KDA LEADERS
              </span>
              {topPlayers.length === 0 ? (
                <p style={{ color: "#999", fontSize: "14px" }}>No top players found.</p>
              ) : (
                [...topPlayers]
                  .sort((a, b) => b.avg_kda - a.avg_kda)
                  .slice(0, 3)
                  .map((player, index) => (
                    <div
                      key={player.player_id}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        padding: "6px 0",
                      }}
                      className="player-performance-box">
                      {/* {index === 0 && (
                        <div style={{ height: "136px" }}>
                          <img
                            src={`/players/${player.player_image}`}
                            alt={player.player_name}
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                          />
                        </div>
                      )} */}

                      {/* Player name */}
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "16px",
                            color: "white",
                            marginBottom: "4px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            cursor: "pointer",
                          }}
                          onClick={() => navigate(`/dashboard/league/${leagueId}/player/${player.player_id}`)}
                          className="player-name-navigate">
                          <img
                            src={`/teams/${player.team_logo}`}
                            alt={player.team_name}
                            style={{ height: "16px", width: "16px", objectFit: "contain" }}
                          />
                          {player.player_name}
                        </div>
                        <div style={{ fontSize: "12px", color: "rgb(190, 205, 227)", display: "flex", alignItems: "center", gap: "6px" }}>
                          <img src={`/roles/${player.role}.svg`} alt={player.role} style={{ width: "12px", height: "12px", objectFit: "contain" }} />
                          <p style={{ margin: "0" }}>|</p>
                          <p style={{ margin: "0" }}>{player.team_name}</p>
                        </div>
                      </div>

                      {/* Avg points */}
                      <div style={{ fontWeight: "700", fontSize: "16px", color: "white", textAlign: "end" }}>
                        {player.avg_kda.toFixed(1)} <b style={{ fontSize: "11px", fontWeight: "400" }}>KDA</b>
                      </div>
                    </div>
                  ))
              )}
            </div>
            <div style={{ background: "#ffffff05", padding: "14px", border: "1px solid #19232f" }}>
              <span className="avg-label" style={{ marginBottom: "10px", display: "block" }}>
                AVG KP%
              </span>
              {topPlayers.length === 0 ? (
                <p style={{ color: "#999", fontSize: "14px" }}>No top players found.</p>
              ) : (
                [...topPlayers]
                  .sort((a, b) => b.avg_kill_participation - a.avg_kill_participation)
                  .slice(0, 3)
                  .map((player, index) => (
                    <div
                      key={player.player_id}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "10px",
                        padding: "6px 0",
                      }}
                      className="player-performance-box">
                      {/* {index === 0 && (
                        <div style={{ height: "136px" }}>
                          <img
                            src={`/players/${player.player_image}`}
                            alt={player.player_name}
                            style={{ width: "100%", height: "100%", objectFit: "contain" }}
                          />
                        </div>
                      )} */}

                      {/* Player name */}
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: "16px",
                            color: "white",
                            marginBottom: "4px",
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            cursor: "pointer",
                          }}
                          onClick={() => navigate(`/dashboard/league/${leagueId}/player/${player.player_id}`)}
                          className="player-name-navigate">
                          <img
                            src={`/teams/${player.team_logo}`}
                            alt={player.team_name}
                            style={{ width: "16px", height: "16px", objectFit: "contain" }}
                          />
                          {player.player_name}
                        </div>
                        <div style={{ fontSize: "11px", color: "rgb(190, 205, 227)", display: "flex", alignItems: "center", gap: "6px" }}>
                          <img src={`/roles/${player.role}.svg`} alt={player.role} style={{ width: "12px", height: "12px", objectFit: "contain" }} />
                          <p style={{ margin: "0" }}>|</p>
                          <p style={{ margin: "0" }}>{player.team_name}</p>
                        </div>
                      </div>

                      {/* Avg points */}
                      <div style={{ fontWeight: "700", fontSize: "16px", color: "white", textAlign: "end" }}>
                        {(player.avg_kill_participation * 100).toFixed(1)}
                        <b style={{ fontSize: "11px", fontWeight: "400" }}>%</b>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const PlayerTable = ({ players, side = "blue" }) => {
  const navigate = useNavigate();
  const { leagueId } = useParams();
  const isRight = side === "red";
  const ROLE_ORDER = {
    Top: 1,
    Jng: 2,
    Mid: 3,
    Bot: 4,
    Sup: 5,
  };

  const sortedPlayers = [...players].sort((a, b) => (ROLE_ORDER[a.role] || 99) - (ROLE_ORDER[b.role] || 99));
  return (
    <div
      className="player-table"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        width: "100%",
      }}>
      {sortedPlayers.map((p) => (
        <div
          key={p.player_id}
          className="player-stat-row"
          style={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
            justifyContent: isRight ? "flex-end" : "flex-start",
            textAlign: isRight ? "right" : "left",
            borderBottom: "1px solid rgb(25, 35, 47)",
            paddingBottom: "6px",
          }}>
          {!isRight && (
            <div className="vs-divider">
              <img src={`/roles/${p.role}.svg`} alt={p.role} style={{ width: "16px", height: "16px", objectFit: "contain", opacity: "0.4" }} />
            </div>
          )}
          <div
            className="player-stat"
            style={{
              order: isRight ? 2 : 0,
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}>
            {/* {!isRight && (
              <img key={p.player_image} src={`/players/${p.player_image}`} alt={p.name} style={{ height: "78px" }} className="player-image-history" />
            )} */}
            <div
              style={{
                height: "42px",
                width: "64px",
                borderRadius: "4px",
                overflow: "hidden",
              }}>
              <img
                src={`/champion/${p.champion_played}.png`}
                alt={p.champion_played}
                style={{
                  height: "100%",
                  width: "100%",
                  objectFit: "cover",
                  transform: "scale(1.1)", // zooms the image inside
                  transformOrigin: "center",
                }}
              />
            </div>
            {/* {isRight && (
              <img key={p.player_image} src={`/players/${p.player_image}`} alt={p.name} style={{ height: "78px" }} className="player-image-history" />
            )} */}
          </div>
          {isRight && (
            <div className="vs-divider" style={{ order: 2 }}>
              <img src={`/roles/${p.role}.svg`} alt={p.role} style={{ width: "16px", height: "16px", objectFit: "contain", opacity: "0.4" }} />
            </div>
          )}
          {/* Player Info */}
          <div
            style={{
              order: 1,
              display: "flex",
              flexDirection: "column",
              gap: "4px",
            }}>
            <h4
              style={{ margin: 0, cursor: "pointer", fontSize: "14px", fontWeight: "700" }}
              onClick={() => navigate(`/dashboard/league/${leagueId}/player/${p.player_id}`)}
              className="player-name-navigate">
              {p.name}
            </h4>
            <div
              className="stat-cell"
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                justifyContent: isRight ? "flex-end" : "flex-start",
                fontSize: "14px",
              }}>
              {!isRight && <img src="/sword-icon.png" alt="Sword" style={{ height: "13px", width: "13px" }} />}
              {p.kills}/{p.deaths}/{p.assists}
              {isRight && <img src="/sword-icon.png" alt="Sword" style={{ height: "13px", width: "13px" }} />}
            </div>
            <div
              className="stat-cell"
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                justifyContent: isRight ? "flex-end" : "flex-start",
              }}>
              {!isRight && <img src="/minions-icon.png" alt="Minions" style={{ height: "13px", width: "13px" }} />}
              {p.cs}
              {isRight && <img src="/minions-icon.png" alt="Minions" style={{ height: "13px", width: "13px" }} />}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default MatchHistoryPage;
